import { SidebarMenu } from '../_models/menu/SidebarMenu.model';
import { flattenMenuLeaves, isMenuCategory } from './menu-organization';

// Sezioni a schede: per le aziende che lo attivano (cmp_menu_org.hubs, editor in
// anagrafica azienda > Organizzazione menu) una voce di menu senza sottomenu apre un
// cruscotto e le pagine della definizione diventano schede mostrate sopra la pagina
// (MenuHubTabsComponent nel layout); quelle pagine spariscono dal menu, ovunque siano.
// Solo presentazione: il menu completo resta quello del preset, quindi permessi e
// PageAccessGuard non cambiano. Le schede si legano alle pagine per url (i pg_id
// cambiano tra ambienti) e compaiono solo se la pagina è nel menu dell'utente.

export interface MenuHubTabDef {
	key: string;
	/** Testo o chiave i18n */
	label: string;
	/**
	 * url delle pagine di menu (come in prt_page.pg_url), nell'ordine delle sotto-schede; ':cmp' sta per
	 * l'id dell'azienda, che alcune pagine hanno nell'url (es. 'hr/hrviewdip/:cmp')
	 */
	urls: string[];
	/**
	 * Pagina che non e' una voce di menu (es. le pagine HR, raggiunte dalle card): la scheda compare se l'utente ha
	 * questa funzione del modulo (stessa regola delle card) e il suo url e' il primo di `urls` con ':cmp' = azienda.
	 * Senza `nome` basta una funzione qualsiasi del modulo (le pagine che senza funzioni mostrano "accesso negato").
	 */
	funzione?: { modulo: string; nome?: string };
	/**
	 * Pagina raggiunta dalle card della pagina padre (es. Prodotti dalla home OnLineShop), senza voce di menu ne'
	 * funzioni: la scheda compare a chi vede la voce `parentUrl` della sezione, come le card.
	 */
	daPadre?: boolean;
}

/** Funzioni dell'utente (mdfu_nome, e chiaveModulo() per i moduli con almeno una funzione) e azienda corrente. */
export interface MenuHubContesto {
	cmpId: number;
	funzioni?: Set<string>;
}

export interface MenuHubDef {
	key: string;
	label: string;
	/**
	 * url della pagina di menu che diventa la voce della sezione (es. Cantieri): perde il
	 * sottomenu delle schede; le altre pagine che il menu (DB) le mette sotto restano il suo sottomenu.
	 * Assente = voce generata dal portale (es. Commesse), messa dove stava la prima delle sue pagine.
	 */
	parentUrl?: string;
	/**
	 * Voce generata per una sezione le cui pagine non sono voci di menu (Finanza aziendale, pagine raggiunte dalle
	 * card di Fatture): compare a chi vede la voce con questo url; i permessi delle schede li verifica la barra.
	 */
	ancoraUrl?: string;
	/** Icona PrimeIcons della voce generata */
	icon?: string;
	/** pagina cruscotto aperta dalla voce di menu */
	homeUrl: string;
	tabs: MenuHubTabDef[];
}

export const MENU_HUBS: MenuHubDef[] = [
	{
		key: 'hr',
		label: 'HR',
		parentUrl: '/hr',
		// il cruscotto e' la pagina HR stessa (riepilogo scadenze + grafici)
		homeUrl: '/hr',
		tabs: [
			// stesse pagine e permessi delle card della pagina HR (hrmanager.component)
			{ key: 'scadenze', label: 'Dipendenti scadenze', urls: ['hr/hrviewdip/:cmp'], funzione: { modulo: 'HRService', nome: 'HR Dipendenti' } },
			{ key: 'dipendenti', label: 'Dipendenti', urls: ['hr/employees/:cmp?mode_view=hr'], funzione: { modulo: 'HRService', nome: 'HR Dipendenti' } },
			{ key: 'corsi', label: 'Corsi aziendali', urls: ['hr/hrviewdip/:cmp/hrviewcorsidip/:cmp/0/0'], funzione: { modulo: 'HRService', nome: 'HR Corsi Azienda' } },
			// voce di menu sotto HR (prt_page 'Presenze', url /records)
			{ key: 'presenze', label: 'Presenze', urls: ['/records'] },
		],
	},
	{
		key: 'gare',
		label: 'Gare',
		parentUrl: '/gare/list',
		homeUrl: '/gare/cruscotto',
		tabs: [{ key: 'lista', label: 'Lista gare', urls: ['/gare/list'] }],
	},
	{
		key: 'cantieri',
		label: 'Cantieri',
		parentUrl: '/worksite/list',
		homeUrl: '/worksite/cruscotto',
		tabs: [
			{ key: 'lista', label: 'Lista cantieri', urls: ['/worksite/list'] },
			{ key: 'rapportini', label: 'Rapportini', urls: ['/worksite/rapportini'] },
			{ key: 'schede', label: 'Schede', urls: ['/worksite/schede-cantieri'] },
			{ key: 'riepilogo', label: 'Riepilogo generale', urls: ['/worksite/riepilogo-generale'] },
		],
	},
	{
		key: 'commesse',
		label: 'Commesse',
		icon: 'pi pi-briefcase',
		homeUrl: '/commesse',
		tabs: [
			{ key: 'contratti', label: 'Gestione contratti', urls: ['gestione-contratti'] },
			{ key: 'sal', label: 'SAL attivi', urls: ['sal'] },
			{ key: 'sal-passivi', label: 'SAL passivi', urls: ['sal-passivo'] },
			{ key: 'cdp', label: 'Certificati di pagamento', urls: ['cert-payment'] },
			{ key: 'subaff', label: 'Sub-affidamenti', urls: ['sub-aff'] },
			{ key: 'cel', label: 'CEL', urls: ['cel'] },
		],
	},
	{
		key: 'fatture',
		label: 'Fatture',
		parentUrl: '/fatture',
		// il cruscotto e' la pagina Fatture stessa (numeri e grafici)
		homeUrl: '/fatture',
		tabs: [
			// stesse pagine e permessi delle card della pagina Fatture (fatture-home.component)
			{ key: 'scadenziario', label: 'Scadenziario', urls: ['fatture/scadenziario/:cmp'], funzione: { modulo: 'Fatture', nome: 'Scadenziario Fatture' } },
			{ key: 'lista', label: 'Fatture', urls: ['fatture/lista/:cmp'], funzione: { modulo: 'Fatture', nome: 'Elenco Fatture' } },
			{ key: 'da-abbinare', label: 'Da abbinare', urls: ['fatture/da-abbinare/:cmp'], funzione: { modulo: 'Fatture', nome: 'Fatture Da Abbinare' } },
			{ key: 'prospetto', label: 'Prospetto scadenziario', urls: ['fatture/prospetto/:cmp'], funzione: { modulo: 'Fatture', nome: 'Prospetto Scadenziario' } },
			{ key: 'attive', label: 'Fatture attive', urls: ['fatture/attive/:cmp'], funzione: { modulo: 'Fatture', nome: 'Fatture Attive' } },
			{ key: 'registro', label: 'Registro fatture emesse', urls: ['fatture/registro-fatture-emesse/:cmp'], funzione: { modulo: 'Fatture', nome: 'Registro Fatture Emesse' } },
		],
	},
	{
		key: 'finanza',
		label: 'Finanza aziendale',
		icon: 'pi pi-wallet',
		ancoraUrl: '/fatture',
		homeUrl: '/finanza',
		tabs: [
			// le pagine che col cruscotto Fatture escono dalla pagina Fatture: stessi permessi delle loro card
			{ key: 'quadro', label: 'Quadro finanziario', urls: ['fatture/quadro-finanziario/:cmp'], funzione: { modulo: 'Fatture', nome: 'Quadro Finanziario' } },
			{ key: 'movimenti', label: 'Movimenti bancari', urls: ['fatture/movimenti-bancari/:cmp'], funzione: { modulo: 'Fatture', nome: 'Movimenti Bancari' } },
			{ key: 'mutui', label: 'Mutui', urls: ['fatture/mutui/:cmp'], funzione: { modulo: 'Fatture', nome: 'Mutui' } },
			{ key: 'clienti', label: 'Gestione clienti', urls: ['fatture/gestione-clienti/:cmp'], funzione: { modulo: 'Fatture', nome: 'Gestione Clienti' } },
			{ key: 'per-cantiere', label: 'fatture.per_cantiere.titolo', urls: ['fatture/fatture-emesse-cantiere/:cmp'], funzione: { modulo: 'Fatture', nome: 'Fatture per Cantiere' } },
			{ key: 'fatturato', label: 'Fatturato cantieri', urls: ['fatture/fatturato-cantieri/:cmp'], funzione: { modulo: 'Fatture', nome: 'Fatturato Cantieri' } },
		],
	},
	{
		key: 'documentale',
		label: 'Documentale',
		parentUrl: 'sezione_documentale',
		homeUrl: '/sezione_documentale/cruscotto',
		tabs: [{ key: 'archivio', label: 'Archivio', urls: ['sezione_documentale'] }],
	},
	{
		key: 'automezzi',
		label: 'Automezzi',
		parentUrl: '/automezzi',
		homeUrl: '/automezzi/cruscotto',
		tabs: [
			// lista e scadenze non sono voci di menu: le apre chi ha le funzioni dei loro moduli (come le pagine stesse)
			{ key: 'lista', label: 'Lista mezzi', urls: ['/automezzi/lista'], funzione: { modulo: 'Lista Automezzi' } },
			{ key: 'scadenze', label: 'Scadenze', urls: ['/automezzi/scadenze'], funzione: { modulo: 'Dashboard Scadenze' } },
			// voce di menu sotto Automezzi (prt_page 'Centri di costo')
			{ key: 'costi', label: 'Centri di costo', urls: ['/automezzi/costi'] },
		],
	},
	{
		// negozio online: le card della home (Prodotti, Tipologie, Ordini) non sono voci di menu, le apre chi vede la voce OnLineShop
		key: 'onlineshop',
		label: 'E-commerce',
		parentUrl: '/onlineshop',
		homeUrl: '/onlineshop/cruscotto',
		tabs: [
			{ key: 'prodotti', label: 'Prodotti', urls: ['/onlineshop/prodotti'], daPadre: true },
			{ key: 'tipologie', label: 'Tipologie prodotti', urls: ['/onlineshop/new-typeprd'], daPadre: true },
			{ key: 'ordini', label: 'Ordini', urls: ['/onlineshop/ordini'], daPadre: true },
		],
	},
	{
		// appuntamenti dei liberi professionisti: lista, calendario e scadenze erano pulsanti della lista (non voci di
		// menu, li apre chi vede la voce Appuntamenti); disponibilita' e tipi servizio sono voci di menu o funzioni
		key: 'appuntamenti',
		label: 'Appuntamenti',
		parentUrl: '/biz-appointments',
		homeUrl: '/biz-appointments/cruscotto',
		tabs: [
			{ key: 'lista', label: 'Lista appuntamenti', urls: ['/biz-appointments/lista'], daPadre: true },
			{ key: 'calendario', label: 'Calendario', urls: ['/biz-appointments/calendario'], daPadre: true },
			{ key: 'scadenze', label: 'Scadenze manutenzioni', urls: ['/biz-appointments/scadenze'], daPadre: true },
			{ key: 'disponibilita', label: 'Disponibilità', urls: ['/biz-appointments/disponibilita'], funzione: { modulo: 'Appuntamenti', nome: 'Disponibilità' } },
			{ key: 'tipi-servizio', label: 'Tipi servizio', urls: ['/biz-appointments/tipi-servizio'], funzione: { modulo: 'Appuntamenti', nome: 'Tipi Servizio' } },
		],
	},

];

/**
 * Interruttori per azienda in Organizzazione menu (salvati in cmp_menu_org.hubs): le sezioni a
 * schede più i cruscotti in cima a una pagina che resta com'è (Aziende: grafici sopra le card).
 */
export const INTERRUTTORI_CRUSCOTTO: { key: string; label: string; descrizione: string }[] = [
	...MENU_HUBS.map((h) => ({ key: h.key, label: h.label, descrizione: 'cruscotto con schede, le pagine delle schede escono dal menu' })),
	{ key: 'aziende', label: 'Aziende', descrizione: 'grafici in cima alla pagina, sopra le card' },
	{ key: 'magazzino', label: 'Magazzino', descrizione: 'scheda Cruscotto con i grafici, prima delle schede della pagina' },
	{ key: 'prenotazioni', label: 'Prenotazioni', descrizione: 'scheda Cruscotto con i grafici, prima delle schede della pagina' },
];

export function findMenuHub(key: string): MenuHubDef | undefined {
	return MENU_HUBS.find((h) => h.key === key);
}

/** url di menu in forma confrontabile: '/a/b', senza query, hash e slash finali. */
export function normMenuUrl(url: any): string {
	if (!url || typeof url !== 'string') {
		return '';
	}
	return '/' + url.split('?')[0].split('#')[0].trim().replace(/^\/+/, '').replace(/\/+$/, '');
}

/** url di una scheda con ':cmp' sostituito dall'azienda corrente. */
export function urlScheda(def: MenuHubTabDef, cmpId: number): string {
	return def.urls[0].replace(/:cmp/g, String(cmpId));
}

/** Voce di MenuHubContesto.funzioni che dice che l'utente ha almeno una funzione del modulo. */
export function chiaveModulo(modulo: string): string {
	return 'modulo:' + modulo;
}

/** Moduli di cui servono le funzioni per le schede fuori menu delle sezioni indicate. */
export function moduliSchede(hubKeys: string[]): string[] {
	const moduli = new Set<string>();
	(hubKeys || []).map(findMenuHub).forEach((h) => h?.tabs.forEach((t) => t.funzione && moduli.add(t.funzione.modulo)));
	return Array.from(moduli);
}

/** Espressione per un url di scheda con ':cmp' (id azienda numerico). */
function urlConAzienda(n: string): RegExp {
	return new RegExp('^' + n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/:cmp/g, '\\d+') + '$');
}

export interface MenuHubTabItem {
	label: string;
	url: string;
}

export interface MenuHubTab {
	key: string;
	label: string;
	items: MenuHubTabItem[];
}

/**
 * Schede della sezione visibili all'utente: pagine della definizione presenti nel suo
 * menu. Gruppi senza pagine → scartati.
 */
export function buildMenuHubTabs(menu: SidebarMenu[], hub: MenuHubDef, contesto?: MenuHubContesto): MenuHubTab[] {
	const byUrl = new Map<string, SidebarMenu>();
	for (const leaf of flattenMenuLeaves(menu)) {
		const u = normMenuUrl(leaf.url);
		if (u !== '/' && !byUrl.has(u)) {
			byUrl.set(u, leaf);
		}
	}
	const tabs: MenuHubTab[] = [];
	for (const def of hub.tabs) {
		const items: MenuHubTabItem[] = [];
		for (const u of def.urls) {
			const n = normMenuUrl(u);
			const leaf = n.includes(':cmp') ? Array.from(byUrl.entries()).find(([k]) => urlConAzienda(n).test(k))?.[1] : byUrl.get(n);
			if (leaf) {
				items.push({ label: leaf.pg_title, url: leaf.url });
			}
		}
		// pagina fuori menu: visibile con la funzione dell'utente (come la card corrispondente)
		if (!items.length && def.funzione && contesto?.cmpId && contesto.funzioni?.has(def.funzione.nome ?? chiaveModulo(def.funzione.modulo))) {
			items.push({ label: def.label, url: urlScheda(def, contesto.cmpId) });
		}
		// pagina delle card della voce padre: visibile a chi vede quella voce
		if (!items.length && def.daPadre && hub.parentUrl && byUrl.has(normMenuUrl(hub.parentUrl))) {
			items.push({ label: def.label, url: def.urls[0] });
		}
		if (items.length) {
			tabs.push({ key: def.key, label: def.label, items });
		}
	}
	return tabs;
}

/** True se l'utente vede almeno una pagina della sezione (per la guardia del cruscotto). */
export function menuHubAccessibile(hub: MenuHubDef, puoAprire: (url: string) => boolean): boolean {
	if (hub.ancoraUrl && puoAprire(normMenuUrl(hub.ancoraUrl))) return true;
	return hub.tabs.some((t) => t.urls.some((u) => !u.includes(':cmp') && puoAprire(normMenuUrl(u))));
}

/**
 * Per ogni voce le pagine di altre sezioni che stanno sotto il suo url (Fatture e le schede di Finanza aziendale,
 * /fatture/mutui/..): finiscono in `escludiUrls`, cosi' su quelle pagine si evidenzia solo la sezione.
 */
function segnaPagineAltrui(menu: SidebarMenu[], sezioni: { home: string; urls: string[] }[]): SidebarMenu[] {
	return menu.map((item) => {
		const u = normMenuUrl(item.url);
		const proprie = new Set((item.hubUrls || []).map(normMenuUrl));
		const altrui = isMenuCategory(item)
			? []
			: sezioni.filter((sz) => sz.home !== u).flatMap((sz) => sz.urls).filter((x) => !proprie.has(x) && x !== u && x.startsWith(u + '/'));
		const submenu = item.submenu?.length ? segnaPagineAltrui(item.submenu, sezioni) : item.submenu;
		return altrui.length ? { ...item, submenu, escludiUrls: altrui } : { ...item, submenu };
	});
}

/**
 * Copia del menu per sidebar/topnav con le sezioni attive:
 * - le pagine-scheda spariscono dal menu, a qualunque livello;
 * - la pagina padre della sezione (Cantieri) apre il cruscotto; le pagine che il menu le
 *   mette sotto e che non sono schede restano il suo sottomenu;
 * - la voce di una sezione senza padre (Commesse) è generata e messa dove stava la
 *   prima delle sue pagine; se quelle pagine stanno in una cartella (profilo menu), la
 *   cartella diventa la voce, con le sue altre voci come sottomenu; se l'utente non ne
 *   vede nessuna, non compare;
 * - la voce di una sezione con le pagine fuori menu (Finanza aziendale) va subito dopo la voce
 *   del primo livello che contiene la sua ancora (Fatture);
 * - cartelle rimaste vuote spariscono, cartelle rimaste con la sola voce di una sezione
 *   lasciano il posto alla voce.
 * Ordine, cartelle e nomi delle altre voci restano quelli del database (prt_page e profilo di
 * Organizzazione menu dell'azienda): qui non si riordina niente.
 * `hubUrls` tiene la voce evidenziata sulle schede. Il menu originale (usato per i
 * permessi) non viene toccato.
 */
export function collapseMenuHubs(menu: SidebarMenu[], hubKeys: string[] | null | undefined, cmpId: number = 0): SidebarMenu[] {
	const hubs = (hubKeys || [])
		.map(findMenuHub)
		.filter((h): h is MenuHubDef => !!h)
		.map((hub, i) => {
			// le pagine fuori menu servono solo a tenere evidenziata la voce: i permessi li verifica la barra delle schede
			const fuoriMenu = cmpId > 0 ? hub.tabs.filter((t) => t.funzione).map((t) => urlScheda(t, cmpId)) : [];
			const tabUrls = [...buildMenuHubTabs(menu, hub).flatMap((t) => t.items.map((it) => it.url)), ...fuoriMenu];
			return { hub, indice: i, tabUrls, tabSet: new Set(tabUrls.map(normMenuUrl)) };
		})
		.filter((h) => h.tabUrls.length);
	if (!hubs.length) {
		return menu;
	}
	const conPadre = (item: SidebarMenu) => hubs.find((h) => h.hub.parentUrl && normMenuUrl(h.hub.parentUrl) === normMenuUrl(item.url));
	const generataDa = (item: SidebarMenu) => hubs.find((h) => !h.hub.parentUrl && h.tabSet.has(normMenuUrl(item.url)));
	const eScheda = (item: SidebarMenu) => hubs.some((h) => h.tabSet.has(normMenuUrl(item.url)));
	const generate = new Set<string>();

	const voceSezione = (h: (typeof hubs)[number], base: Partial<SidebarMenu>, submenu: SidebarMenu[] = []): SidebarMenu =>
		({ ...base, url: h.hub.homeUrl, submenu, hubUrls: [h.hub.homeUrl, ...h.tabUrls] }) as SidebarMenu;

	const visit = (items: SidebarMenu[]): SidebarMenu[] => {
		const out: SidebarMenu[] = [];
		for (const item of items || []) {
			const padre = conPadre(item);
			if (padre) {
				// le schede escono; le altre pagine che il menu le mette sotto restano il suo sottomenu
				out.push(voceSezione(padre, item, visit(item.submenu || [])));
				continue;
			}
			const generata = generataDa(item);
			if (generata && !generate.has(generata.hub.key)) {
				generate.add(generata.hub.key);
				out.push(
					voceSezione(generata, {
						id: -1 - generata.indice,
						pg_id: -1 - generata.indice,
						name: generata.hub.label,
						pg_title: generata.hub.label,
						styleclass: generata.hub.icon || item.styleclass,
						pg_posizione: item.pg_posizione,
					}),
				);
				continue;
			}
			if (eScheda(item)) {
				continue;
			}
			if (!item.submenu?.length) {
				out.push(item);
				continue;
			}
			// cartella (del profilo menu) con dentro le pagine di una sezione generata: diventa la voce della
			// sezione, col nome e l'icona della cartella e per sottomenu le sue altre voci (es. Commesse con
			// Listini prezzi e Prezziario primus)
			const sezioneCartella = isMenuCategory(item) ? (item.submenu || []).map(generataDa).find((h) => !!h && !generate.has(h.hub.key)) : undefined;
			if (sezioneCartella) {
				generate.add(sezioneCartella.hub.key);
				const altre = visit((item.submenu || []).filter((s) => !sezioneCartella.tabSet.has(normMenuUrl(s.url))));
				out.push(voceSezione(sezioneCartella, { ...item, name: item.name || sezioneCartella.hub.label, pg_title: item.pg_title || sezioneCartella.hub.label, styleclass: item.styleclass || sezioneCartella.hub.icon }, altre));
				continue;
			}
			const submenu = visit(item.submenu);
			if (isMenuCategory(item) && submenu.length === 1 && submenu[0].hubUrls) {
				// cartella rimasta con la sola voce di una sezione: al suo posto la voce stessa
				out.push({ ...submenu[0], styleclass: submenu[0].styleclass || item.styleclass });
			} else if (submenu.length || !isMenuCategory(item)) {
				out.push({ ...item, submenu });
			}
		}
		return out;
	};
	const visitato = visit(menu);
	// sezioni con le pagine fuori menu (Finanza aziendale): voce subito dopo la voce del primo livello che contiene
	// l'ancora, se l'utente la vede
	const contiene = (items: SidebarMenu[], url: string): boolean => items.some((i) => normMenuUrl(i.url) === url || contiene(i.submenu || [], url));
	for (const h of hubs) {
		const ancora = h.hub.ancoraUrl ? visitato.findIndex((item) => contiene([item], normMenuUrl(h.hub.ancoraUrl))) : -1;
		if (ancora >= 0 && !generate.has(h.hub.key)) {
			generate.add(h.hub.key);
			visitato.splice(ancora + 1, 0, voceSezione(h, { id: -1 - h.indice, pg_id: -1 - h.indice, name: h.hub.label, pg_title: h.hub.label, styleclass: h.hub.icon }));
		}
	}
	return segnaPagineAltrui(visitato, hubs.map((h) => ({ home: normMenuUrl(h.hub.homeUrl), urls: [h.hub.homeUrl, ...h.tabUrls].map(normMenuUrl) })));
}
